import { useState, useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { NavLink } from 'react-router-dom';
import { Camera, BarChart3, Users, Home, LogOut, FileText, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';

const navigationItems = [
  { title: 'Dashboard', url: '/photographers', icon: Home },
  { title: 'I Miei Eventi', url: '/photographers/events', icon: Camera },
  { title: 'Vendite', url: '/photographers/sales', icon: BarChart3 },
  { title: 'Clienti', url: '/photographers/customers', icon: Users },
  { title: 'Report', url: '/photographers/reports', icon: FileText },
];

const PhotographerSidebarContent = ({ onItemClick }: { onItemClick?: () => void }) => {
  const location = useLocation();
  const getNavClassName = ({ isActive }: { isActive: boolean }) =>
    isActive ? "bg-muted text-primary font-medium" : "hover:bg-muted/50";

  return (
    <SidebarContent>
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold">Fotografo Panel</h2>
      </div>
      <SidebarGroup>
        <SidebarGroupLabel>Navigazione</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {navigationItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild>
                  <NavLink to={item.url} end className={getNavClassName} onClick={onItemClick}>
                    <item.icon className="mr-2 h-4 w-4" />
                    <span>{item.title}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>
  );
};

export const PhotographerLayout = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) { navigate('/auth'); return; }

      const { data: hasPhotographerRole, error: roleError } = await supabase
        .rpc('has_role', { _user_id: user.id, _role: 'photographer' });

      if (roleError || !hasPhotographerRole) {
        toast({ title: "Accesso negato", description: "Non hai i permessi per accedere a questa area", variant: "destructive" });
        navigate('/');
        return;
      }
      setUser(user);
    } catch (error) {
      console.error('Photographer access check error:', error);
      navigate('/auth');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background overflow-hidden">
        {!isMobile && (
          <Sidebar className="w-60" collapsible="icon">
            <PhotographerSidebarContent />
            <div className="mt-auto p-4 border-t">
              <Button variant="ghost" onClick={handleLogout} className="w-full justify-start">
                <LogOut className="mr-2 h-4 w-4" />
                Esci
              </Button>
            </div>
          </Sidebar>
        )}

        <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
          <header className="h-16 border-b bg-card flex items-center px-2 md:px-6">
            {isMobile && (
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="mr-2">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0">
                  <PhotographerSidebarContent onItemClick={() => setIsMobileMenuOpen(false)} />
                  <div className="p-4 border-t">
                    <Button variant="ghost" onClick={handleLogout} className="w-full justify-start text-destructive">
                      <LogOut className="mr-2 h-4 w-4" />
                      Esci
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            )}
            {!isMobile && <SidebarTrigger className="mr-4" />}
            <div className="flex-1">
              <h1 className="text-base md:text-lg font-semibold truncate">Panel Fotografo</h1>
              <p className="text-xs md:text-sm text-muted-foreground truncate">
                Benvenuto, {user?.user_metadata?.first_name || user?.email}
              </p>
            </div>
            {isMobile && (
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-destructive">
                <LogOut className="h-4 w-4" />
              </Button>
            )}
          </header>
          <main className="flex-1 p-2 md:p-6 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};
